"use client";

/**
 * The card header (title, spinner, share actions, refresh/clear) and the
 * no-summary-yet prompt.
 *
 * Copy / download / listen / export all live in ONE shared action bar
 * (`components/ai/summary-actions`) so every summary surface offers the same set,
 * named the same way — see `SummaryActions` for why that consolidation exists.
 */

import React from "react";
import { RefreshCw, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { SummaryActions } from "@/components/ai/summary-actions";

import { SUMMARY_ICON_COLOR_CLASS } from "./config";
import { EMPTY_STATE_PROMPT, type BriefingKind } from "./generation-narration";
import type { SummaryTypeConfig } from "./types";

export interface SummaryCardHeaderProps {
  presentation: SummaryTypeConfig;
  busy: boolean;
  /** Non-empty once there is a completed body worth sharing. */
  shareableMarkdown: string | null;
  entityType?: string;
  entityId?: string;
  showRefreshButton: boolean;
  showClearButton: boolean;
  hasSummary: boolean;
  onRefresh: () => void;
  onGenerate: () => void;
  onClear: () => void;
}

export function SummaryCardHeader({
  presentation,
  busy,
  shareableMarkdown,
  entityType,
  entityId,
  showRefreshButton,
  showClearButton,
  hasSummary,
  onRefresh,
  onGenerate,
  onClear,
}: SummaryCardHeaderProps) {
  const Icon = presentation.icon;

  return (
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${SUMMARY_ICON_COLOR_CLASS[presentation.color]}`} />
          <CardTitle className="text-lg md:text-xl">{presentation.label}</CardTitle>
          {busy && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <div className="flex items-center gap-2">
          {shareableMarkdown && (
            <SummaryActions
              title={presentation.label}
              markdown={shareableMarkdown}
              entityType={entityType || undefined}
              entityId={entityId}
            />
          )}
          {showRefreshButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={hasSummary ? onRefresh : onGenerate}
              disabled={busy}
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          {showClearButton && hasSummary && (
            <Button variant="ghost" size="sm" onClick={onClear} disabled={busy}>
              Clear
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm md:text-base text-muted-foreground leading-snug">
        {presentation.description}
      </p>
    </CardHeader>
  );
}

/** Shown only when there is no summary, nothing in flight, and no error. */
export function EmptySummaryPrompt({
  briefingKind,
  disabled,
  onGenerate,
}: {
  briefingKind: BriefingKind;
  disabled: boolean;
  onGenerate: () => void;
}) {
  return (
    <div className="text-center py-8">
      <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
      <p className="text-sm md:text-base text-muted-foreground mb-4 max-w-md mx-auto leading-relaxed md:leading-[1.55]">
        {EMPTY_STATE_PROMPT[briefingKind]}
      </p>
      <Button onClick={onGenerate} disabled={disabled}>
        <Sparkles className="h-4 w-4 mr-2" />
        Generate AI Summary
      </Button>
    </div>
  );
}
